import { graphqlRequest, graphqlUpload } from "./client";
import {
  GATE_FIELDS,
  JOB_FIELDS,
  PROFILE_FIELDS,
  type Job,
  type KycStatus,
  type PlatformProfile,
  type StepGate,
} from "./types";

export interface PaidReadyView {
  step: {
    payMethods: string[];
    payConfirmed: boolean;
  };
  profiles: PlatformProfile[];
  gate: StepGate;
  done: boolean;
}

export interface LaunchPlan {
  name: string;
  trackLabel: string;
  workEnabledAt: string;
  niche: string;
  profiles: PlatformProfile[];
  portfolioUrl: string;
  payMethods: string[];
  bestBid?: { job: Job; pct: number } | null;
}

const PAID_FIELDS = `step { payMethods payConfirmed } profiles { ${PROFILE_FIELDS} } ${GATE_FIELDS} done`;

/** Step 4: Verify (payment setup and KYC proof make the participant Work-Enabled). */
export async function fetchPaidReady(): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ getPaidReady: PaidReadyView }>(`{ getPaidReady { ${PAID_FIELDS} } }`)
  ).getPaidReady;
}

/** Save payment methods and the verified-account confirmation. */
export async function savePayment(methods: string[], confirmed: boolean): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ savePayment: PaidReadyView }>(
      `mutation($input: SavePaymentInput!) { savePayment(input: $input) { ${PAID_FIELDS} } }`,
      { variables: { input: { methods, confirmed } } },
    )
  ).savePayment;
}

/** Set the KYC status on a verified platform. */
export async function setKycStatus(platformId: string, status: KycStatus): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ setKycStatus: PaidReadyView }>(
      `mutation($input: SetKycStatusInput!) { setKycStatus(input: $input) { ${PAID_FIELDS} } }`,
      { variables: { input: { platformId, status } } },
    )
  ).setKycStatus;
}

/** Upload a KYC status screenshot. */
export async function uploadKycScreenshot(platformId: string, file: File): Promise<PaidReadyView> {
  return (
    await graphqlUpload<{ uploadKycScreenshot: PaidReadyView }>(
      `mutation($platformId: String!, $file: Upload!) { uploadKycScreenshot(platformId: $platformId, file: $file) { ${PAID_FIELDS} } }`,
      { platformId },
      "file",
      file,
    )
  ).uploadKycScreenshot;
}

/** Complete Step 4 and become Work-Enabled. */
export async function completePaidReady(): Promise<PaidReadyView> {
  return (
    await graphqlRequest<{ completePaidReady: PaidReadyView }>(
      `mutation { completePaidReady { ${PAID_FIELDS} } }`,
    )
  ).completePaidReady;
}

/** The Launch Plan, available once Work-Enabled. */
export async function fetchLaunchPlan(): Promise<LaunchPlan> {
  return (
    await graphqlRequest<{ getLaunchPlan: LaunchPlan }>(
      `{ getLaunchPlan { name trackLabel workEnabledAt niche portfolioUrl payMethods
        profiles { ${PROFILE_FIELDS} }
        bestBid { pct job { ${JOB_FIELDS} } } } }`,
    )
  ).getLaunchPlan;
}
